import { useState, useEffect, useRef } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "./ui/button";
import { getAssetChains } from "../services/tradingDataService";

import { useAuth } from "../context/AuthContext";
import coin from "../assets/images/placeholder/crypto-colour.png";
import AssetList from "../components/AssetList";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
  DialogClose,
} from "./ui/dialog";
import { useAvailableAssets } from "../hooks/query/queries";
import { logger } from "../utils/logger";

export function CurrencySelector({
  userId: propUserId,
  onSelect,
  onSelectNet,
  isSend,
  isEightyeightPay,
  hotActionAsset,
  setSelectedAsset,
  selectedAsset,
  disableButton,
  showAvailableBalance = true,
  showLabel = true,
  rate = 0,
  hasError = false,
  /** When set (e.g. business suite), use these rows instead of personal `useAvailableAssets` */
  assetsOverride,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [network, setNetwork] = useState([]);
  const { userProfile: contextUserProfile } = useAuth();

  const onSelectRef = useRef(onSelect);
  const onSelectNetRef = useRef(onSelectNet);
  onSelectRef.current = onSelect;
  onSelectNetRef.current = onSelectNet;

  const effectiveUserId = propUserId || contextUserProfile?._id;

  const balanceParams = {
    id: effectiveUserId,
    type: "Crypto",
  };

  const balanceParamsEightEight = {
    id: effectiveUserId,
  };

  /** When an array (including []), use it only—do not fall back to personal portfolio. */
  const hasAssetsOverride = Array.isArray(assetsOverride);

  const { data: assetsFromHook, isLoading: hookLoading } = useAvailableAssets({
    isSend,
    isEightyeightPay,
    balanceParams,
    balanceParamsEightEight,
    enabled: !hasAssetsOverride,
  });
  const assetsOwned = hasAssetsOverride ? assetsOverride : assetsFromHook;
  const isLoading = hasAssetsOverride ? false : hookLoading;

  const filteredAssets = assetsOwned?.filter(
    (assets) =>
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleSelectNet = (net) => {
    if (onSelectNet) {
      onSelectNet(net);
    }
    setOpen(false);
  };

  const handleSelectSend = (asset) => {
    if (!asset || !asset?.assetId || !asset?.asset) {
      return;
    }
    setSelectedAsset(asset);
    if (onSelect) {
      onSelect(asset);
    }
  };

  useEffect(() => {
    if (!hotActionAsset) return;

    if (!hotActionAsset?.assetId || !hotActionAsset?.asset) {
      return;
    }

    setSelectedAsset(hotActionAsset);
    if (onSelectRef.current) {
      onSelectRef.current(hotActionAsset);
    }

    const assetId = hotActionAsset?.assetId;
    const matchedNetwork = network.find((net) => net.assetId === assetId);
    if (matchedNetwork && onSelectNetRef.current) {
      onSelectNetRef.current(matchedNetwork);
    }
    setOpen(false);
  }, [hotActionAsset, network, setSelectedAsset]);

  useEffect(() => {
    if (!assetsOwned) return;

    const chain = async () => {
      try {
        const results = await Promise.all(
          assetsOwned?.map(async (asset) => {
            const chainData = await getAssetChains(asset.assetId);
            return chainData || [];
          })
        );
        const combinedNetworks = results.flat();
        setNetwork(combinedNetworks);
      } catch (error) {
        logger.error("Error Fetching networks", error);
      }
    };
    chain();
  }, [assetsOwned]);

  const handleSelect = (asset) => {
    if (!asset || !asset?.assetId || !asset?.asset) {
      return;
    }
    setOpen(false);
    setSelectedAsset(asset);
    if (onSelect) {
      onSelect(asset);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 grid-rows-1">
      {isEightyeightPay === true && (
        <div
          className={`${showLabel ? "space-y-2" : ""} ${
            showAvailableBalance ? "md:col-span-1" : "md:col-span-3"
          }`}
        >
          {showLabel && (
            <label className="text-gray-600 text-sm">Select asset</label>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              {isLoading ? (
                <div className="w-full flex items-center justify-between py-4 px-4 rounded-2xl border border-gray-300 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />
                    <div className="flex flex-col gap-1">
                      <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                    </div>
                  </div>
                  <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                </div>
              ) : (
                <Button
                  disabled={disableButton}
                  variant="outline"
                  className={`w-full h-[70px] md:h-[88px] flex items-center justify-between p-[24px] rounded-[16px] hover:border-brand-primary transition-colors ${
                    hasError ? "border-[#DA2F58]" : "border-[#CECBD7]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center">
                      <img
                        src={
                          selectedAsset && selectedAsset.assetIcon
                            ? selectedAsset.assetIcon
                            : coin
                        }
                        alt={
                          selectedAsset
                            ? selectedAsset.assetName
                            : "Default Asset"
                        }
                        width={40}
                        height={40}
                        className="text-white max-w-full h-auto"
                      />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-[16px] text-[#423E51]">
                        {selectedAsset?.asset
                          ? selectedAsset.asset.toUpperCase()
                          : "Select..."}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="text-gray-400 w-3 h-3" />
                </Button>
              )}
            </DialogTrigger>
            <DialogPortal>
              <DialogOverlay className="bg-brand-primary/45 backdrop-blur-[4px] fixed inset-0" />
              <DialogContent
                className="max-w-[95vw] md:max-w-md rounded-lg md:mx-auto"
                aria-describedby="saved-addresses-description"
              >
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    className="w-8 md:w-10 h-8 md:h-10 absolute top-2 md:top-4 right-2 md:right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                  >
                    <X size={16} className="md:size-[18px] text-gray-700" />
                  </Button>
                </DialogClose>
                <DialogHeader className="px-4 md:px-6">
                  <DialogTitle className="text-base md:text-lg font-semibold">
                    {"Choose Asset"}
                  </DialogTitle>
                </DialogHeader>
                <div
                  id="saved-addresses-description"
                  className="mt-4 px-4 md:px-6"
                >
                  {assetsOwned?.length > 0 && (
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Search addresses..."
                        className="w-full border rounded-lg px-3 py-2 text-sm md:text-base"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="flex flex-col space-y-3 max-h-60 md:max-h-72 overflow-y-auto">
                    {filteredAssets?.length > 0 ? (
                      filteredAssets?.map((asset) => (
                        <div
                          key={asset.assetId}
                          className="border p-3 rounded-lg mb-2 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition"
                          onClick={() => handleSelect(asset)}
                        >
                          <div className="flex gap-3 items-center">
                            <div>
                              <img
                                src={asset.assetIcon}
                                alt={asset.assetName}
                                width={32}
                                height={32}
                                className="rounded-full flex-col w-8 h-8 md:w-8 md:h-8"
                              />
                            </div>
                            <div>
                              <p className="text-gray-800 font-semibold text-sm md:text-base">
                                {asset.asset.toUpperCase()}
                              </p>
                              <p className="text-xs md:text-sm text-gray-600">
                                {asset.assetName}
                              </p>
                            </div>
                          </div>
                          <div className="font-semibold text-sm md:text-base">
                            {asset.asset?.toUpperCase() === "BTC"
                              ? asset.availableAmount.toFixed(6)
                              : asset.availableAmount.toFixed(2)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">
                        No Assets Found
                      </p>
                    )}
                  </div>
                </div>
              </DialogContent>
            </DialogPortal>
          </Dialog>
        </div>
      )}
      {isSend === true && (
        <div
          className={`space-y-2 ${
            showAvailableBalance ? "md:col-span-1" : "md:col-span-3"
          }`}
        >
          <label className="text-gray-600 text-sm">Select asset</label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              {isLoading ? (
                <div className="w-full flex items-center justify-between py-4 px-4 rounded-2xl border border-gray-300 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />
                    <div className="flex flex-col gap-1">
                      <Skeleton className="w-20 h-5 rounded-full bg-gray-300" />
                    </div>
                  </div>
                  <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-between py-4 md:py-8 rounded-2xl hover:border-brand-primary transition-colors !h-[70px] md:!h-[88px] px-4 md:px-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 md:w-10 h-[70px] md:h-[88px] rounded-full flex items-center justify-center">
                      <img
                        src={
                          selectedAsset && selectedAsset.assetIcon
                            ? selectedAsset.assetIcon
                            : coin
                        }
                        alt={
                          selectedAsset
                            ? selectedAsset.assetName
                            : "Default Asset"
                        }
                        width={40}
                        height={40}
                        className="text-white max-w-full h-auto"
                      />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm md:text-base">
                        {selectedAsset?.asset
                          ? selectedAsset.asset.toUpperCase()
                          : "Select..."}
                      </div>
                      {selectedAsset?.assetName && (
                        <p className="text-xs md:text-sm text-gray-600">
                          {selectedAsset?.assetName}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronDown className="text-gray-400 w-4 h-4 md:w-5 md:h-5" />
                </Button>
              )}
            </DialogTrigger>
            <DialogPortal>
              <DialogOverlay className="bg-brand-primary/45 backdrop-blur-[4px] fixed inset-0" />
              <DialogContent
                className="max-w-[95vw] md:max-w-md rounded-lg md:mx-auto"
                aria-describedby="saved-addresses-description"
              >
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    className="w-8 h-8 md:w-10 md:h-10 absolute top-2 md:top-4 right-2 md:right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                  >
                    <X size={16} className="md:size-[18px] text-gray-700" />
                  </Button>
                </DialogClose>
                <DialogHeader className="px-4 md:px-6">
                  <DialogTitle className="text-base md:text-lg font-semibold">
                    {"Choose Asset"}
                  </DialogTitle>
                </DialogHeader>
                {assetsOwned?.length > 0 ? (
                  <div
                    id="saved-addresses-description"
                    className="mt-4 px-4 md:px-6"
                  >
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Search Assets..."
                        className="w-full border rounded-lg px-3 py-2 text-sm md:text-base"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="overflow-y-auto max-h-[300px] px-2">
                      <AssetList
                        ownedAssets={filteredAssets}
                        network={network}
                        handleSelect={handleSelectSend}
                        handleSelectNet={handleSelectNet}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    No Assets found
                  </p>
                )}
              </DialogContent>
            </DialogPortal>
          </Dialog>
        </div>
      )}
      {showAvailableBalance === true && (
        <div className="space-y-2 flex flex-col col-span-1 md:col-span-2">
          <label className="text-[#6D6584] text-sm">Available balance</label>
          <div className="py-3 md:py-4 px-4 md:px-6 rounded-2xl bg-gray-50 flex items-center h-[70px] md:h-[88px]">
            <div className="text-lg md:text-xl font-semibold text-[#6D6584] flex justify-between items-center w-full gap-2 md:gap-0">
              <p className="text-base md:text-lg">
                {(() => {
                  if (selectedAsset?.availableAmountFormatted) {
                    return selectedAsset.availableAmountFormatted;
                  }
                  if (
                    selectedAsset?.availableAmount !== undefined &&
                    selectedAsset?.availableAmount !== null
                  ) {
                    const assetSymbol = selectedAsset?.asset?.toUpperCase();
                    const decimals = assetSymbol === "BTC" ? 6 : 2;
                    return new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: decimals,
                      maximumFractionDigits: decimals,
                    })
                      .format(selectedAsset.availableAmount)
                      .replace(/,/g, " ");
                  }
                  return "0.00";
                })()}
              </p>
              <p className="text-sm md:text-base">
                ≈ R{" "}
                {(() => {
                  let amountValue = 0;
                  if (selectedAsset?.availableAmountFormatted) {
                    amountValue = parseFloat(
                      selectedAsset.availableAmountFormatted
                    );
                  } else if (
                    selectedAsset?.availableAmount !== undefined &&
                    selectedAsset?.availableAmount !== null
                  ) {
                    amountValue = selectedAsset.availableAmount;
                  }
                  if (amountValue > 0 && rate) {
                    return (amountValue * rate).toFixed(2);
                  }
                  return "0.00";
                })()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
